import { useEffect, useState } from 'react';

export interface HeatmapPoint {
    lat: number;
    lng: number;
    value: number;
}

export interface HeatmapData {
    max: number;
    min: number;
    data: HeatmapPoint[];
}

export const useHeatmap = (mapRef: any, coordinatesOfPointsForHeatmap: HeatmapPoint[], shouldShowHeatmap: boolean) => {
    const [heatmapInstance, setHeatmapInstance] = useState(null);

    const cleanupHeatmap = (heatmapInstanceToClean: any) => {
        if (heatmapInstanceToClean) {
            try {
                if (typeof heatmapInstanceToClean.setMap === 'function') {
                    heatmapInstanceToClean.setMap(null);
                }

                if (heatmapInstanceToClean.listeners) {
                    heatmapInstanceToClean.listeners.forEach(listener => {
                        google.maps.event.removeListener(listener);
                    });
                }

                if (heatmapInstanceToClean.container) {
                    heatmapInstanceToClean.container.remove();
                }

                if (heatmapInstanceToClean.getData) {
                    heatmapInstanceToClean.setData([]);
                }

                if (heatmapInstanceToClean.data) {
                    heatmapInstanceToClean.data = null;
                }

            } catch (error) {
                console.error('Error during heatmap cleanup:', error);
            }
        }
    };

    const createHeatmapLayer = (map: google.maps.Map, data: HeatmapPoint[]) => {
        return new Promise<any>((resolve) => {
            const checkAndCreateHeatmap = () => {
                if (typeof (window as any).HeatmapOverlay !== 'undefined') {
                    const heatmapLayer = new (window as any).HeatmapOverlay(map, {
                        radius: 30,
                        maxOpacity: 0.8,
                        minOpacity: 0.1,
                        scaleRadius: false,
                        useLocalExtrema: true,
                        latField: 'lat',
                        lngField: 'lng',
                        valueField: 'value',
                        gradient: {
                            0.45: 'rgb(0,0,255)',
                            0.55: 'rgb(0,255,255)',
                            0.65: 'rgb(0,255,0)',
                            0.95: 'yellow',
                            1.0: 'rgb(255,0,0)'
                        }
                    });

                    setTimeout(() => {
                        const heatmapData: HeatmapData = {
                            max: 1,
                            min: 0,
                            data: data.map(point => ({
                                lat: parseFloat(point.lat.toString()),
                                lng: parseFloat(point.lng.toString()),
                                value: point.value
                            }))
                        };

                        heatmapLayer.setData(heatmapData);
                        resolve(heatmapLayer);
                    }, 50);
                } else {
                    setTimeout(checkAndCreateHeatmap, 100);
                }
            };

            checkAndCreateHeatmap();
        });
    };

    useEffect(() => {
        if (!shouldShowHeatmap || coordinatesOfPointsForHeatmap.length === 0 || !mapRef) {
            if (heatmapInstance) {
                cleanupHeatmap(heatmapInstance);
                setHeatmapInstance(null);
            }
            return;
        }

        const mapComponent = mapRef()?.current;
        if (mapComponent) {
            try {
                const map = mapComponent.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;

                if (heatmapInstance) {
                    cleanupHeatmap(heatmapInstance);
                    setHeatmapInstance(null);
                }

                setTimeout(() => {
                    createHeatmapLayer(map, coordinatesOfPointsForHeatmap)
                        .then((heatmapLayer) => {
                            setHeatmapInstance(heatmapLayer);
                        })
                        .catch((error) => {
                            console.error('Error creating heatmap:', error);
                        });
                }, 100);

            } catch (error) {
                console.error('Error creating heatmap:', error);
            }
        }
    }, [shouldShowHeatmap, coordinatesOfPointsForHeatmap, mapRef]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (heatmapInstance) {
                cleanupHeatmap(heatmapInstance);
            }
        };
    }, []);

    return {
        heatmapInstance,
        isHeatmapActive: shouldShowHeatmap && coordinatesOfPointsForHeatmap.length > 0
    };
};
